import React, { createContext, useState, useContext } from 'react';
import { handleNewEventMixPanel } from '@utils/helpers';
import { formatCurrency } from '@utils/numbers';
import { yupValidate } from '@utils/validators';
import {  useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { CIBudgetPerUnit } from '@utils/helpers';
import {
  validationSchema,
  campaignFormModel,
  INITIAL_CONTEXT,
  steps,
  campaignObjectiveEvent,
  formInitialValues,
} from './constants';
import { useCampaign } from '@utils/contexts';
import {useShowToast} from "@hooks/Common";
import {CampaignService} from "@services";

const CampaignFormStepContext = createContext(INITIAL_CONTEXT);

export const CampaignFormStepProvider = ({ children }) => {
  const authenticationReducer = useSelector((state) => state.authenticationReducer);
  const match = useRouteMatch();
  const showToast = useShowToast();
  const [formData, setFormData] = useState(INITIAL_CONTEXT.data);
  const [file, setFile] = useState([]);

  const [groups, setGroups] = useState([]);
  const [groupsName, setGroupsName] = useState([]);

  const [getTags, setGetTags] = useState([]);

  const [leadForm, setLeadForm] = useState('');
  const [currentStep, setCurrentStep] = useState(INITIAL_CONTEXT.currentStep);

  const [shoulShowRequiredGroups, setShouldShowRequiredGroups] = useState(false);
  const [shouldShowRequiredFile, setShouldShowRequiredFile] = useState(false);
  const [mediaLibraryFolderForm, setMediaLibraryFolderForm] = useState('');

  const isLastStep = currentStep === steps.length - 1;
  const { id, img } = steps[currentStep];

  const [creationLoading, setCreationLoading] = useState(false);

  const [showComunicationCard, setShowComunicationCard] = useState(false);

  const dispatchEvent = (event, eventType) => {
    handleNewEventMixPanel(event, eventType, authenticationReducer, match);
  };

  const {
    campaigns: {mutateCampaigns}
  } = useCampaign();


  const setStepEvents = () => {
    if (currentStep === 0) dispatchEvent('click_finish_step_one_ci_button', 'view');
    if (currentStep === 1) dispatchEvent('click_finish_step_two_ci_button', 'view');
  };

  function skipStep() {
    const units = JSON.parse(localStorage.getItem('preSelectedUnitsCI'));
    const hasPreSelectedUnits = units?.length;

    if (hasPreSelectedUnits && currentStep === 0) {
      localStorage.removeItem('preSelectedUnitsCI');
      setGroups(units.map((unit) => unit.source_id));
      setGroupsName(units.map((unit) => unit.name));
      setShowComunicationCard(true);
      return true;
    }

    return false;
  }

  function nextStep() {
    if (currentStep === 0 && file.length < 1) {
      setShouldShowRequiredFile(true);
      return;
    }
    if (currentStep === 1 && groups.length < 1) {
      setShouldShowRequiredGroups(true);
      return;
    }
    if (skipStep()) {
      setCurrentStep(2);
      setStepEvents();
      return;
    }
    setStepEvents();
    setCurrentStep(currentStep + 1);
  }

  function previousStep() {
    dispatchEvent('click_return_step_ci_button', 'view');
    setCurrentStep(currentStep - 1);
  }

  async function createCampaign() {
    setCreationLoading(true);
    dispatchEvent('click_confirm_create_campaign_ci_button', 'view');

    const tzoffset = new Date().getTimezoneOffset() * 60000;
    const { max_budget, startDate, endDate, campaign_lead_form, default_budget, tags } = formData;
    const { files, blobUrls } = file[0];
    const shouldRenderDefaultBudget =
      default_budget !== '' &&
      formatCurrency({ value: max_budget }) !== '.' &&
      formatCurrency({ value: max_budget }) !== '0.00';

    const shouldRenderMaxBudget =
      max_budget !== '' &&
      formatCurrency({ value: max_budget }) !== '.' &&
      formatCurrency({ value: max_budget }) !== '0.00';

    const body = {
      ...formData,
      start_time: new Date(startDate).toISOString(),
      end_time: new Date(new Date(endDate) - tzoffset).toISOString().slice(0, -1),
      creative_specs: { lead_form_id: campaign_lead_form },
      ...(shouldRenderMaxBudget && {
        max_budget: formatCurrency({ value: max_budget }),
      }),
      ...(shouldRenderDefaultBudget && {
        default_budget: formatCurrency({ value: default_budget }),
      }),
      permitted_groups: groups ? groups : [],
      media_type: files.length && files[0].file.type.includes('image') ? 'SINGLE_PICTURE' : 'VIDEO',
      medias: blobUrls.map((item) => item.url),
    };

    if (tags) body.tags = [tags];
    if (!shouldRenderMaxBudget) delete body.max_budget;
    if (!shouldRenderDefaultBudget) delete body.default_budget;

    if (formData.category.category) {
      dispatchEvent('fill_ci_category', 'fill');
    }
    if (formData.category.sku) {
      dispatchEvent('fill_ci_sku', 'fill');
    }
    if (formData.category.model) {
      dispatchEvent('fill_ci_product', 'fill');
    }
    if (formData.default_budget !== 'R$0,00' && formData.default_budget) {
      dispatchEvent('fill_ci_unitybudget', 'fill');
    }
    dispatchEvent(campaignObjectiveEvent(formData.campaign_objective), 'view');
    const response = await CampaignService.createCampaign(body)
    if (response?.data?.is_success){
      mutateCampaigns()
      showToast('Campanha criada com sucesso!', 'success')
    } else {
      showToast('Erro ao criar campanha, tente novamente.', 'error')
    }
    setCreationLoading(false);
  }

  async function checkIsNextStepAble({ values }) {
    const hasBudgetPerUnit = CIBudgetPerUnit.includes(authenticationReducer.subDomain.sub_domain);
    if (currentStep === 0) {
      if (hasBudgetPerUnit) return true;
      const response = await yupValidate({ schema: validationSchema, data: values });
      return response.isValid && file.length;
    } else if (currentStep === 1) {
      return groups.length >= 1;
    }
    return true;
  }

  return (
    <CampaignFormStepContext.Provider
      value={{
        formData,
        setFormData,
        currentStep,
        nextStep,
        previousStep,
        file,
        setFile,
        groups,
        setGroups,
        id,
        img,
        isLastStep,
        submitForm: createCampaign,
        creationLoading,
        shouldShowRequiredFile,
        setShouldShowRequiredFile,
        shoulShowRequiredGroups,
        setShouldShowRequiredGroups,
        groupsName,
        setGroupsName,
        leadForm,
        setLeadForm,
        dispatchEvent,
        mediaLibraryFolderForm,
        setMediaLibraryFolderForm,
        setGetTags,
        getTags,
        showComunicationCard,
        checkIsNextStepAble,
        validationSchema,
        campaignFormModel,
        formInitialValues,
      }}
    >
      {children}
    </CampaignFormStepContext.Provider>
  );
};

export const useCampaignFormStep = () => {
  const context = useContext(CampaignFormStepContext);

  if (!context) throw new Error('Expected to be wrapped in a CampaignFormStep');

  return context;
};
